<template>
  <q-dialog
    :model-value="modelValue"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <q-card
      ref="detailCard"
      class="usp-guid-detail-card"
      :style="cardPositionStyle"
    >
      <div
        class="bg-teal-8 text-white usp-guid-detail-card__bar"
        @mousedown="onDragStart"
      >
        <div class="usp-guid-detail-card__bar-row">
          <q-icon name="receipt_long" size="18px" class="usp-guid-detail-card__icon" />
          <span class="usp-guid-detail-card__bar-title">Свод УСП</span>
          <q-space />
          <q-btn icon="close" flat dense no-caps size="md" label="Закрыть" v-close-popup />
        </div>
      </div>

      <div v-if="data.loading" class="usp-guid-detail-card__loading">
        <q-spinner size="30px" color="teal" />
      </div>

      <div v-else class="usp-guid-detail-card__body">
        <div class="q-px-sm q-pt-sm">
          <div class="text-caption text-grey-6">
            гуид: <span class="text-grey-8 usp-guid-detail-card__guid">{{ data.uspGuid }}</span>
          </div>
        </div>

        <q-markup-table dense flat separator="cell" class="q-mt-sm">
          <thead>
            <tr style="background:#e0f2f1">
              <th class="text-left">Период</th>
              <th class="text-right">Сумма СМР</th>
              <th class="text-right">Сумма Материал</th>
              <th class="text-left">Контрагент</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(r, ri) in data.rows" :key="ri">
              <td class="text-left text-caption">{{ fmtDateRu(r.value_date) }}</td>
              <td class="text-right mono">{{ fmtNum(r.sum_smr) }}</td>
              <td class="text-right mono">{{ fmtNum(r.sum_materials) }}</td>
              <td class="text-left text-caption usp-guid-detail-card__contractor">{{ r.contractor_name || '—' }}</td>
            </tr>
            <tr v-if="!data.rows.length">
              <td colspan="4" class="text-center text-grey-5">Нет данных</td>
            </tr>
          </tbody>
        </q-markup-table>
      </div>
    </q-card>
  </q-dialog>
</template>

<script>
import { useFormatters } from '../composables/useFormatters';

const CARD_WIDTH = 640;

export default {
  name: 'PpmUspGuidDetailDialog',
  props: {
    modelValue: Boolean,
    data: { type: Object, default: () => ({ show: false, loading: false, uspGuid: '', rows: [] }) },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      posX: null,
      posY: null,
      isDragging: false,
      dragOffsetX: 0,
      dragOffsetY: 0,
    };
  },
  setup() {
    const { fmtNum, fmtDateRu } = useFormatters();
    return { fmtNum, fmtDateRu };
  },
  computed: {
    cardPositionStyle() {
      const x = this.posX ?? Math.max(16, window.innerWidth - CARD_WIDTH - 16);
      const y = this.posY ?? 48;
      return {
        position: 'fixed',
        left: `${x}px`,
        top: `${y}px`,
        margin: 0,
        width: `${CARD_WIDTH}px`,
        maxWidth: '92vw',
        maxHeight: 'calc(100vh - 32px)',
      };
    },
  },
  watch: {
    modelValue(val) {
      if (val) {
        this.posX = null;
        this.posY = null;
        this.$nextTick(() => this.initPosition());
      } else {
        this.stopDrag();
      }
    },
    'data.loading'(loading) {
      if (!loading && this.modelValue) {
        this.$nextTick(() => this.clampPosition());
      }
    },
  },
  beforeUnmount() {
    this.stopDrag();
  },
  methods: {
    getCardElement() {
      const ref = this.$refs.detailCard;
      return ref?.$el || ref;
    },
    initPosition() {
      const card = this.getCardElement();
      const cardWidth = card?.offsetWidth || CARD_WIDTH;
      const cardHeight = card?.offsetHeight || 320;
      this.posX = Math.max(16, window.innerWidth - cardWidth - 16);
      this.posY = Math.max(16, Math.min(window.innerHeight - cardHeight - 16, 48));
    },
    clampPosition() {
      const card = this.getCardElement();
      if (!card) return;
      const cardWidth = card.offsetWidth || CARD_WIDTH;
      const cardHeight = card.offsetHeight;
      const x = this.posX ?? Math.max(16, window.innerWidth - cardWidth - 16);
      const y = this.posY ?? 48;
      this.posX = Math.min(Math.max(0, x), Math.max(0, window.innerWidth - cardWidth));
      this.posY = Math.min(Math.max(0, y), Math.max(0, window.innerHeight - cardHeight));
    },
    onDragStart(event) {
      if (event.button !== 0) return;
      if (event.target.closest('button')) return;

      const card = this.getCardElement();
      if (!card) return;

      const rect = card.getBoundingClientRect();
      this.posX = rect.left;
      this.posY = rect.top;
      this.isDragging = true;
      this.dragOffsetX = event.clientX - this.posX;
      this.dragOffsetY = event.clientY - this.posY;

      document.addEventListener('mousemove', this.onDragMove);
      document.addEventListener('mouseup', this.onDragEnd);
    },
    onDragMove(event) {
      if (!this.isDragging) return;

      const card = this.getCardElement();
      const cardWidth = card?.offsetWidth || CARD_WIDTH;
      const cardHeight = card?.offsetHeight || 320;
      const maxX = Math.max(0, window.innerWidth - cardWidth);
      const maxY = Math.max(0, window.innerHeight - cardHeight);

      this.posX = Math.min(maxX, Math.max(0, event.clientX - this.dragOffsetX));
      this.posY = Math.min(maxY, Math.max(0, event.clientY - this.dragOffsetY));
    },
    onDragEnd() {
      this.isDragging = false;
      document.removeEventListener('mousemove', this.onDragMove);
      document.removeEventListener('mouseup', this.onDragEnd);
    },
    stopDrag() {
      this.isDragging = false;
      document.removeEventListener('mousemove', this.onDragMove);
      document.removeEventListener('mouseup', this.onDragEnd);
    },
  },
};
</script>

<style scoped lang="scss">
.usp-guid-detail-card {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.usp-guid-detail-card__bar {
  cursor: move;
  user-select: none;
  flex-shrink: 0;
  padding: 8px 12px;
}

.usp-guid-detail-card__bar-row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 28px;
}

.usp-guid-detail-card__icon {
  flex-shrink: 0;
}

.usp-guid-detail-card__bar-title {
  font-size: 13px;
  font-weight: 500;
}

.usp-guid-detail-card__loading {
  padding: 32px;
  text-align: center;
}

.usp-guid-detail-card__body {
  overflow-y: auto;
  max-height: calc(100vh - 96px);
}

.usp-guid-detail-card__guid {
  word-break: break-all;
}

.usp-guid-detail-card__contractor {
  max-width: 200px;
  white-space: normal;
}
</style>
